import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';

import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ModalDialogComponent } from '@pedix-workspace/angular-ui-modal';
import { IconClockComponent } from '@pedix-workspace/angular-ui-icons';
import { EstablishmentClosedReason, TemporaryClosure, WorkingDays } from '@pedix-workspace/utils';
import { AlertComponent } from '@pedix-workspace/angular-ui-alert';
import { WorkingHoursMessageDetailsComponent } from '@pedix-workspace/pedixapp-shared-catalog';
import { format } from 'date-fns';

export type EstablishmentClosedDialogParams = {
  reason: EstablishmentClosedReason;
  workingDays: WorkingDays | null;
  temporaryClosure?: TemporaryClosure | null;
};

@Component({
    selector: 'pxw-establishment-closed-dialog',
    templateUrl: './establishment-closed-dialog.component.html',
    styleUrls: ['./establishment-closed-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoDirective,
        ModalDialogComponent,
        IconClockComponent,
        AlertComponent,
        WorkingHoursMessageDetailsComponent,
    ]
})
export class EstablishmentClosedDialogComponent {
  private t = inject(TranslocoService);

  @ViewChild(ModalDialogComponent) modalDialog: ModalDialogComponent;

  readonly reason = signal<EstablishmentClosedReason>('working-hours');
  readonly workingDays = signal<WorkingDays | null>(null);
  readonly temporaryClosure = signal<TemporaryClosure | null>(null);

  readonly description = computed(() => {
    switch (this.reason()) {
      case 'inventory':
        return this.t.translate('workingHours.inventoryClosedDescription');
      case 'temporary':
        return this.temporaryClosureMessage();
      default:
        return this.t.translate('establishmentClosedDialog.description');
    }
  });

  readonly showWorkingHoursDetails = computed(
    () => this.reason() === 'working-hours' && !!this.workingDays()?.isActive,
  );

  open(params: EstablishmentClosedDialogParams) {
    this.reason.set(params.reason);
    this.workingDays.set(params.workingDays);
    this.temporaryClosure.set(params.temporaryClosure ?? null);

    return this.modalDialog.open().catch(() => {});
  }

  private temporaryClosureMessage(): string {
    const temporaryClosure = this.temporaryClosure();

    if (temporaryClosure?.message) {
      return temporaryClosure.message;
    }
    if (temporaryClosure?.to) {
      return this.t.translate('workingHours.temporaryClosedDescription', {
        returnDate: format(temporaryClosure.to, 'dd/MM/yy'),
      });
    }
    return this.t.translate('establishmentClosedDialog.description');
  }
}
